import i18n from '@/shared/i18n';

export type TimeUnit = 'y' | 'd' | 'h' | 'min' | 's';

const currentLocale = (): string => (i18n.language?.startsWith('en') ? 'en-GB' : 'fr-FR');

export const formatTime = (seconds: number | null, minUnit: TimeUnit = 's'): string => {
  if (seconds === null || seconds === undefined) return '—';

  const units: Array<{ unit: TimeUnit; label: string; value: number }> = [
    { unit: 'y', label: i18n.t('ui.timeUnits.year'), value: 365 * 24 * 60 * 60 },
    { unit: 'd', label: i18n.t('ui.timeUnits.day'), value: 24 * 60 * 60 },
    { unit: 'h', label: i18n.t('ui.timeUnits.hour'), value: 60 * 60 },
    { unit: 'min', label: i18n.t('ui.timeUnits.minute'), value: 60 },
    { unit: 's', label: i18n.t('ui.timeUnits.second'), value: 1 },
  ];

  // Trouver l'index de l'unité minimale
  const minUnitIndex = units.findIndex((u) => u.unit === minUnit);
  if (minUnitIndex === -1) return '—';

  // Si le temps est inférieur à l'unité minimale, arrondir vers le bas
  const minUnitValue = units[minUnitIndex].value;
  if (seconds < minUnitValue) {
    return `0${units[minUnitIndex].label}`;
  }

  // Calculer le temps pour chaque unité
  let remainingSeconds = Math.floor(seconds);
  const parts: string[] = [];
  let hasReachedSignificantUnit = false;

  for (let i = 0; i <= minUnitIndex; i++) {
    const { label, value } = units[i];
    const amount = Math.floor(remainingSeconds / value);

    if (amount > 0 || hasReachedSignificantUnit) {
      hasReachedSignificantUnit = true;
      parts.push(`${amount}${label}`);
      remainingSeconds %= value;

      // Pour une meilleure lisibilité, on s'arrête après 2 unités
      if (parts.length === 2) break;
    }
  }

  // Si on n'a trouvé aucune partie significative mais qu'on a du temps
  if (parts.length === 0 && remainingSeconds > 0) {
    const lastUnit = units[minUnitIndex];
    return `${Math.floor(seconds / lastUnit.value)}${lastUnit.label}`;
  }

  return parts.join(' ') || `0${units[minUnitIndex].label}`;
};

/**
 * Retourne la date formatée en "DD/MM/YYYY" ou "—" si la date est nulle.
 */
export const formatDate = (date: Date | string | null): string => {
  if (!date) return '—';
  const parsedDate = new Date(date);
  return parsedDate.toLocaleDateString(currentLocale(), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

export const formatPrice = (price: number | null): string => {
  if (price === null) return '—';
  return `${price.toFixed(2)} €`;
};

export const formatPercentage = (percentage: number | null): string => {
  if (percentage === null) return '—';
  return `${Math.round(percentage)}%`;
};

export const formatNumber = (num: number | null): string => {
  if (num === null) return '—';
  return num.toLocaleString(currentLocale());
};
